import QRCode from 'qrcode';

/**
 * Renders a printable "QR card" PNG: title row ("Bärenfang" + brand mark),
 * the QR itself with the brand mark embedded in the centre, and a
 * human-readable footer URL. Returns a base64 PNG data URL.
 *
 *   encoded:   what gets encoded INTO the QR (e.g. login URL, gate URL)
 *   footerUrl: human-readable hint printed under the QR (typically the
 *              public calendar host)
 *
 * QR error-correction is "H" so the ~22 %-of-width centre overlay still
 * leaves the code scannable.
 */

const BRAND_COLOR = '#1f2937';

function drawBrandIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 100, size / 100);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(50, 8);
  ctx.lineTo(85, 28);
  ctx.lineTo(85, 72);
  ctx.lineTo(50, 92);
  ctx.lineTo(15, 72);
  ctx.lineTo(15, 28);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(50, 58, 13, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  for (const [cx, cy] of [
    [34, 43],
    [44, 37],
    [56, 37],
    [66, 43],
  ] as const) {
    ctx.beginPath();
    ctx.arc(cx, cy, 4.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

export async function renderQrCard(encoded: string, footerUrl: string): Promise<string> {
  const QR_SIZE = 560;
  const PADDING = 40;
  const TITLE_H = 96;
  const URL_H = 44;
  const W = QR_SIZE + 2 * PADDING;
  const H = PADDING + TITLE_H + PADDING + QR_SIZE + PADDING + URL_H + PADDING;

  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, encoded, {
    width: QR_SIZE,
    margin: 0,
    errorCorrectionLevel: 'H',
  });

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  const titleY = PADDING;
  const titleIconSize = 56;
  const titleFont =
    '600 42px system-ui, -apple-system, "Segoe UI", "Helvetica Neue", sans-serif';
  ctx.font = titleFont;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = BRAND_COLOR;
  const titleText = 'Bärenfang';
  const textWidth = ctx.measureText(titleText).width;
  const titleGap = 18;
  const groupW = titleIconSize + titleGap + textWidth;
  const groupX = (W - groupW) / 2;
  drawBrandIcon(
    ctx,
    groupX,
    titleY + (TITLE_H - titleIconSize) / 2,
    titleIconSize,
    BRAND_COLOR,
  );
  ctx.fillText(titleText, groupX + titleIconSize + titleGap, titleY + TITLE_H / 2);

  const qrY = PADDING + TITLE_H + PADDING;
  ctx.drawImage(qrCanvas, PADDING, qrY);

  // Centre logo overlay. The hexagon shape itself is the QR clearing —
  // no square sticker — and a white halo around the outline keeps the
  // dark stroke from touching adjacent QR modules. Order is load-bearing:
  // (1) wide white stroke = halo, (2) white fill = clear QR pixels inside,
  // (3) drawBrandIcon redraws the dark stroke + paw on top of the halo.
  const logoSize = Math.round(QR_SIZE * 0.24);
  const cx = W / 2;
  const cy = qrY + QR_SIZE / 2;
  const s = logoSize / 100;
  ctx.beginPath();
  ctx.moveTo(cx, cy - 42 * s);
  ctx.lineTo(cx + 35 * s, cy - 22 * s);
  ctx.lineTo(cx + 35 * s, cy + 22 * s);
  ctx.lineTo(cx, cy + 42 * s);
  ctx.lineTo(cx - 35 * s, cy + 22 * s);
  ctx.lineTo(cx - 35 * s, cy - 22 * s);
  ctx.closePath();
  ctx.lineJoin = 'round';
  ctx.lineWidth = 20 * s;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  drawBrandIcon(ctx, cx - logoSize / 2, cy - logoSize / 2, logoSize, BRAND_COLOR);

  const urlY = qrY + QR_SIZE + PADDING;
  ctx.font =
    '500 22px system-ui, -apple-system, "Segoe UI", "Helvetica Neue", sans-serif';
  ctx.fillStyle = '#475569';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(footerUrl, W / 2, urlY + URL_H / 2);

  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';

  return canvas.toDataURL('image/png');
}
